import React, { useContext, useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { LanguageContext } from '../LanguageSwitcher/LanguageContext'

/**
 * Project card, after the case-study cards on gojilabs.com: a still image
 * that turns into the project's clip on hover, with the project's headline
 * figures over the bottom of the image (↑ arrow, number, label) and an ↗
 * square in the corner. At rest the figures sit faintly on the image; on
 * hover a dark gradient rises, they brighten and the numbers count up.
 *
 * The figures are always in the DOM with their final values — counting only
 * runs while hovered — so nothing depends on an animation to be readable.
 * On touch screens (no hover) they are shown at full strength.
 */

// Split "40,000+" into prefix, number and suffix so only the number counts.
const parseFigure = (value) => {
  const match = /^([^\d]*)([\d,.]+)(.*)$/.exec(value)
  if (!match) return null
  const target = Number(match[2].replace(/,/g, ''))
  if (!Number.isFinite(target)) return null
  const decimals = (match[2].split('.')[1] || '').length
  return { prefix: match[1], target, suffix: match[3], decimals, grouped: match[2].includes(',') }
}

const formatNumber = (n, { decimals, grouped }) => {
  const fixed = n.toFixed(decimals)
  return grouped ? Number(fixed).toLocaleString('en-US', { minimumFractionDigits: decimals }) : fixed
}

const CountUp = ({ value, active }) => {
  const reduced = useReducedMotion()
  const figure = parseFigure(value)
  const [shown, setShown] = useState(value)

  useEffect(() => {
    if (!active || reduced || !figure) {
      setShown(value)
      return undefined
    }
    let frame
    const start = performance.now()
    const duration = 900
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setShown(`${figure.prefix}${formatNumber(figure.target * eased, figure)}${figure.suffix}`)
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
    // figure is derived from value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reduced, value])

  return <span className='work-stat-number'>{shown}</span>
}

const WorkCard = ({ project, onOpen }) => {
  const { translations: t } = useContext(LanguageContext)
  const videoRef = useRef(null)
  const reduced = useReducedMotion()
  const [hovered, setHovered] = useState(false)
  const [playing, setPlaying] = useState(false)

  const enter = () => {
    setHovered(true)
    if (!project.video || reduced || !videoRef.current) return
    setPlaying(true)
    const attempt = videoRef.current.play()
    // Browsers reject autoplay in some contexts; fall back to the still.
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => setPlaying(false))
    }
  }

  const leave = () => {
    setHovered(false)
    if (!project.video || !videoRef.current) return
    videoRef.current.pause()
    videoRef.current.currentTime = 0
    setPlaying(false)
  }

  const stats = (project.stats || []).slice(0, 2)

  return (
    <div
      className={`home-work-card${hovered ? ' is-hovered' : ''}`}
      onClick={() => onOpen(project.link)}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={leave}
      role='link'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(project.link)
        }
      }}
    >
      <div className='home-work-img-frame'>
        <motion.img
          src={project.img}
          alt={project.title}
          className='home-work-img'
          loading='lazy'
          width='640'
          height='400'
          animate={{ opacity: playing ? 0 : 1 }}
          transition={{ duration: 0.35 }}
        />

        {project.video && (
          <video
            ref={videoRef}
            className='home-work-video'
            src={project.video}
            poster={project.img}
            muted
            loop
            playsInline
            preload='none'
            aria-hidden='true'
            style={{ opacity: playing ? 1 : 0 }}
          />
        )}

        <span className='home-work-tag'>{project.tag}</span>

        {stats.length > 0 && (
          <div className='work-stats'>
            {stats.map((stat) => (
              <div className='work-stat' key={stat.value + stat.label}>
                <p className='work-stat-value'>
                  <span className='work-stat-arrow' aria-hidden='true'>↑</span>
                  <CountUp value={stat.value} active={hovered} />
                </p>
                <p className='work-stat-label'>{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        <span className='work-go' aria-hidden='true' title={t.workOpen}>↗</span>
      </div>

      <h3 className='home-work-card-title'>
        {project.title} <span className='home-work-arrow'>→</span>
      </h3>
      <p className='home-work-card-txt'>{project.text}</p>
    </div>
  )
}

export default WorkCard
